import { Tooltip, Typography } from "@mui/material";
import { formatToCurrency } from "@shared/utils/formatters";
import {
  Card,
  ProductInfo,
  ProductNameContainer,
  Price,
  OfferInfo,
  ProductDescription,
  TotalInfo,
  TotalItem,
  CollectPaymentDetailsContainer,
  StyledDivider,
} from "./styles";
import { formatDistanceToNow } from "date-fns";
import { OfferPlanProps } from "./PriceBreakdown.types";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const formatDate = (date: Date) => {
  return formatDistanceToNow(date, { addSuffix: true });
};

export const OfferPlanPriceBreakdown = (props: OfferPlanProps) => {
  const {
    price,
    productName,
    productDescription,
    currency = "USD",
    offer,
    creditCardRequired,
    hideMarketingCopy,
  } = props;
  const priceInDollars = price / 100;
  const finalPriceInDollars = offer.price ? offer.price / 100 : priceInDollars;
  const discountPercentage = offer.price
    ? Number(100 - (100 * finalPriceInDollars) / priceInDollars).toFixed(0)
    : null;

  const isValidOfferPrice = Number(discountPercentage) > 0;

  const showTrialInfo = !!offer?.trialDays && !hideMarketingCopy;

  return (
    <Card>
      {!hideMarketingCopy && (
        <>
          <ProductInfo>
            <ProductNameContainer>
              <Typography>{productName}</Typography>
            </ProductNameContainer>
            {!!productDescription && (
              <ProductDescription>{productDescription}</ProductDescription>
            )}
          </ProductInfo>
          <StyledDivider sx={{ margin: "0 1rem" }} />
        </>
      )}
      {showTrialInfo && (
        <ProductInfo>
          <OfferInfo>
            <Tooltip
              title={
                offer.validUntil
                  ? `Offer ends ${formatDate(offer.validUntil)}`
                  : "Offer available for a limited time"
              }
            >
              <Typography
                component="span"
                variant="inherit"
                data-testid="offer-trial-period-info"
              >
                Try Sidekick free for {offer.trialDays} days
              </Typography>
            </Tooltip>
          </OfferInfo>
          {!creditCardRequired ? (
            <CollectPaymentDetailsContainer>
              <Typography variant="body2">
                Start your free trial with no credit card.
              </Typography>
              <Typography variant="body2">
                Payment details are only required if you continue after the
                trial.
              </Typography>
            </CollectPaymentDetailsContainer>
          ) : (
            <CollectPaymentDetailsContainer>
              <Typography variant="body2">
                Start your free trial with a credit card.
              </Typography>
              <Typography variant="body2">
                You will not be charged until the trial ends, and you can cancel
                anytime during the trial period.
              </Typography>
            </CollectPaymentDetailsContainer>
          )}
        </ProductInfo>
      )}

      <TotalInfo>
        {!!offer.trialDays && (
          <>
            <TotalItem>
              <Typography>{offer.trialDays}-day trial</Typography>
              <Typography fontWeight={700}>Free</Typography>
            </TotalItem>
            <StyledDivider />
          </>
        )}

        {isValidOfferPrice && (
          <>
            <TotalItem>
              <Typography>Subtotal</Typography>
              <Typography
                color={"secondary"}
                fontWeight={700}
                data-testid="offer-price-before-discount"
              >
                {currency.toUpperCase()} {formatToCurrency(priceInDollars)}
              </Typography>
            </TotalItem>
            <TotalItem>
              <Typography>
                Discount{" "}
                <Tooltip
                  title={
                    offer.validUntil
                      ? `Offer ends ${formatDate(offer.validUntil)}`
                      : "Offer available for a limited time"
                  }
                >
                  <InfoOutlinedIcon fontSize="small" color="secondary" />
                </Tooltip>
              </Typography>
              <Typography
                color={"secondary"}
                fontWeight={700}
                data-testid="offer-discount-percentage"
              >
                -{currency.toUpperCase()}{" "}
                {formatToCurrency(priceInDollars - finalPriceInDollars)} (-
                {discountPercentage}%)
              </Typography>
            </TotalItem>
            <StyledDivider />
          </>
        )}

        <TotalItem>
          <Typography>{offer.trialDays ? "Then" : "Total"}</Typography>
          <Price>
            <span data-testid="offer-price-after-discount">
              {currency.toUpperCase()} {formatToCurrency(finalPriceInDollars)} /
              month
            </span>
          </Price>
        </TotalItem>
      </TotalInfo>
    </Card>
  );
};
